import { useCallback, useEffect, useRef, useState, type TouchEvent } from "react";
import { Capacitor } from "@capacitor/core";
import { Link, useNavigate, useParams } from "react-router-dom";
import { KEY_OPTIONS, parseChart, transposeChart } from "../../../shared/chords";
import { durationLabel, formatDateTime, formatTime } from "../../../shared/time";
import type { Attachment, PlanDetail, SongDetail } from "../../../shared/types";
import { useSession } from "../../shell/session";
import { logoUrl } from "../../branding";
import { Dialog } from "../../ui";
import { getSong as fetchSong, messageOf, saveAnnotation, saveStandFont } from "../songs/music-api";
import { beatsInMeter, CHART_MODES, concertKey as shiftKey, safeKey, semitoneGap, writtenKey, type ChartMode } from "../songs/music-theory";
import { AnnotationLayer, colorsFor, type DrawTool } from "./annotate";
import { GuideAudio } from "./audio-guide";
import { getNotes, getPosition, getSong as readCachedSong, listPendingNotes, putNotes, putPosition, putSong, readSessionMemory, writeSessionMemory, type StandMemory } from "./cache";
import { downloadPlanForOffline, getOfflineReadiness } from "./download";
import { ChartView } from "./ChartView";
import { loadStand } from "./load-stand";
import { clamp, nextPdfPage, nextScrollTop } from "./logic";
import { createMetronome, type Metronome } from "./metronome";
import { PdfChart } from "./pdf-chart";
import { useAnnotations } from "./use-annotations";
import { useScreenAwake } from "./wake";
import "./stand.css";

const KINDS: Record<string, string> = { song: "Song", header: "Header", reading: "Reading", media: "Media", break: "Break" };

interface FollowMessage {
  planId: string;
  itemId: string;
  page: number;
  extra: number;
  capo: number;
  view: "chart" | "pdf";
  sender: string;
}

function thisTab(): string {
  const key = "mb-stand-tab";
  const existing = sessionStorage.getItem(key);
  if (existing) return existing;
  const created = crypto.randomUUID();
  sessionStorage.setItem(key, created);
  return created;
}

function filesOf(item: { attachments: Attachment[] }, kind: Attachment["kind"]): Attachment[] {
  return item.attachments.filter((file) => file.kind === kind);
}

export function StandPage() {
  const { planId = "" } = useParams();
  const navigate = useNavigate();
  const { user } = useSession();
  const requestedItem = useRef(new URLSearchParams(window.location.search).get("item"));
  const sender = useRef(thisTab());
  const scroller = useRef<HTMLDivElement>(null);
  const channel = useRef<BroadcastChannel | null>(null);
  const metroRef = useRef<Metronome | null>(null);
  const alive = useRef(true);
  const fontTouched = useRef(false);
  const rememberedFont = useRef(false);
  const skipPageReset = useRef(true);
  const keepPage = useRef(false);
  const followRef = useRef<"off" | "lead" | "follow">("off");
  const planRef = useRef<PlanDetail | null>(null);
  const helpRef = useRef(false);
  const actions = useRef<{
    go: (direction: 1 | -1) => void;
    exit: () => void;
    toggleMetro: () => void;
    nudgeFont: (delta: number) => void;
  }>({
    go: () => {},
    exit: () => {},
    toggleMetro: () => {},
    nudgeFont: () => {},
  });

  const [plan, setPlan] = useState<PlanDetail | null>(null);
  const [library, setLibrary] = useState<SongDetail | null>(null);
  const [practice, setPractice] = useState(false);
  const [offline, setOffline] = useState(false);
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);
  const [cacheNote, setCacheNote] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [offlineReady, setOfflineReady] = useState(false);
  const [index, setIndex] = useState(0);
  const [page, setPage] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [view, setView] = useState<"chart" | "pdf">("chart");
  const [mode, setMode] = useState<ChartMode>("stacked");
  const [extras, setExtras] = useState<Record<string, number>>({});
  const [capo, setCapo] = useState(0);
  const [font, setFont] = useState(user?.preferences.standFont ?? 24);
  const [listOpen, setListOpen] = useState(() => window.matchMedia("(min-width: 981px)").matches);
  const [controlsOpen, setControlsOpen] = useState(false);
  const listOpenRef = useRef(listOpen);
  const [tool, setTool] = useState<DrawTool>("pan");
  const [color, setColor] = useState("#f6f1e8");
  const [penWidth, setPenWidth] = useState(2.5);
  const [markWidth, setMarkWidth] = useState(16);
  const [clearArmed, setClearArmed] = useState(false);
  const [scrolling, setScrolling] = useState(false);
  const [speed, setSpeed] = useState(36);
  const [metroOn, setMetroOn] = useState(false);
  const [beat, setBeat] = useState(0);
  const [tempos, setTempos] = useState<Record<string, number>>({});
  const [follow, setFollow] = useState<"off" | "lead" | "follow">("off");
  const [heard, setHeard] = useState(false);
  const [help, setHelp] = useState(false);
  const [arrangementPick, setArrangementPick] = useState<string | null>(null);
  const [pdfPick, setPdfPick] = useState("");
  const [audioPick, setAudioPick] = useState("");
  const [night, setNight] = useState(true);
  const [zoom, setZoom] = useState(1.25);
  const screenAwake = useScreenAwake();
  const wake = screenAwake.status === "on" ? "Screen stay-awake is on." : screenAwake.status === "off" ? "Screen stay-awake is off." : screenAwake.status === "trying" ? "Trying to keep the screen awake." : "Stay-awake is unavailable here. Use the device's own display timeout.";

  const item = plan?.items[Math.min(index, Math.max((plan?.items.length ?? 1) - 1, 0))] ?? null;
  const sameSong = Boolean(item?.songId) && library?.id === item?.songId;
  const arrangement = sameSong
    ? library?.arrangements.find((row) => row.id === (arrangementPick ?? item?.arrangementId))
      ?? library?.arrangements.find((row) => row.id === item?.arrangementId)
    : undefined;
  const chordpro = arrangement?.chordpro ?? item?.chordpro ?? "";
  const written = writtenKey(chordpro, arrangement?.keyName ?? item?.songDefaultKey ?? item?.keyName ?? "C");
  const planned = safeKey((arrangementPick ? arrangement?.keyName : null) ?? item?.keyName ?? arrangement?.keyName ?? written);
  const extra = item ? extras[item.id] ?? 0 : 0;
  const concert = shiftKey(planned, extra);
  let chart: ReturnType<typeof transposeChart> | null = null;
  if (chordpro.trim()) {
    try {
      chart = transposeChart(chordpro, written, concert, capo);
    } catch {
      chart = null;
    }
  }
  const pdfs = item ? filesOf(item, "chart-pdf") : [];
  const audios = item ? filesOf(item, "audio") : [];
  const pdf = pdfs.find((file) => file.id === pdfPick) ?? pdfs[0] ?? null;
  const audio = audios.find((file) => file.id === audioPick) ?? audios[0] ?? null;
  const showingPdf = view === "pdf" && Boolean(pdf);
  const notePage = showingPdf ? page : 0;
  const canMark = Boolean(item?.songId && (showingPdf || chart));
  const marks = useAnnotations(item?.songId ?? null, arrangement?.id ?? item?.arrangementId ?? null, practice ? null : plan?.id ?? null, notePage, canMark);
  const meter = arrangement?.meter ?? item?.meter ?? "4/4";
  const tempo = item ? tempos[item.id] ?? arrangement?.bpm ?? item.bpm ?? 96 : 96;
  const beats = beatsInMeter(meter);
  const palette = colorsFor(tool);
  const ink = palette.includes(color) ? color : palette[0] ?? "#f6f1e8";
  const width = tool === "highlight" ? markWidth : penWidth;
  followRef.current = follow;
  planRef.current = plan;
  helpRef.current = help;
  listOpenRef.current = listOpen;

  if (!metroRef.current) metroRef.current = createMetronome();

  const rememberPages = useCallback((count: number) => setPageCount(count), []);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      metroRef.current?.stop();
    };
  }, []);

  useEffect(() => {
    const narrow = window.matchMedia("(max-width: 980px)");
    if (narrow.matches) setListOpen(false);
    const onChange = () => { if (narrow.matches) setListOpen(false); };
    narrow.addEventListener("change", onChange);
    return () => narrow.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    let cancelled = false;
    skipPageReset.current = true;
    setPlan(null);
    setReady(false);
    setError("");
    setLibrary(null);
    void (async () => {
      try {
        const result = await loadStand(planId);
        if (cancelled) return;
        const saved = readSessionMemory(planId) ?? await getPosition(planId);
        const requested = requestedItem.current;
        const itemId = requested || saved?.itemId || "";
        const found = result.plan.items.findIndex((row) => row.id === itemId);
        setIndex(found >= 0 ? found : 0);
        const keepPlace = Boolean(saved) && (!requested || requested === saved?.itemId);
        if (saved && keepPlace) applyMemory(saved);
        else setPage(0);
        setPractice(result.practice);
        setOffline(result.offline);
        setPlan(result.plan);
        setReady(true);
      } catch (caught) {
        if (!cancelled) setError(messageOf(caught));
      }
    })();
    return () => { cancelled = true; };
  }, [planId]);

  function applyMemory(saved: StandMemory) {
    setPage(saved.page);
    setCapo(clamp(saved.capo, 0, 11));
    setView(saved.view === "pdf" ? "pdf" : "chart");
    if (CHART_MODES.some((row) => row.id === saved.mode)) setMode(saved.mode as ChartMode);
    if (saved.font) {
      rememberedFont.current = true;
      setFont(clamp(saved.font, 16, 48));
    }
    if (saved.itemId) setExtras({ [saved.itemId]: saved.extra });
    if (saved.itemId && saved.tempo) setTempos({ [saved.itemId]: saved.tempo });
    if (saved.arrangementId) setArrangementPick(saved.arrangementId);
  }

  useEffect(() => {
    if (rememberedFont.current || !user) return;
    rememberedFont.current = true;
    setFont(user.preferences.standFont);
  }, [user]);

  useEffect(() => {
    if (!fontTouched.current || !user) return;
    const handle = window.setTimeout(() => {
      void saveStandFont(clamp(font, 16, 48)).catch(() => undefined);
    }, 700);
    return () => window.clearTimeout(handle);
  }, [font, user]);

  useEffect(() => {
    if (!item?.songId) return;
    let cancelled = false;
    const songId = item.songId;
    void fetchSong(songId).then((song) => {
      if (cancelled) return;
      setLibrary(song);
      void putSong(song);
    }).catch(async () => {
      const cached = await readCachedSong(songId);
      if (!cancelled && cached) setLibrary(cached);
    });
    return () => { cancelled = true; };
  }, [item?.songId]);

  useEffect(() => {
    if (offline) return;
    let cancelled = false;
    const sync = async () => {
      const queued = await listPendingNotes().catch(() => []);
      for (const { key, notes } of queued) {
        if (cancelled) return;
        const parts = key.split("|");
        if (parts.length !== 4 || !parts[0]) continue;
        const pageIndex = Number(parts[3]);
        if (!Number.isInteger(pageIndex) || pageIndex < 0) continue;
        try {
          const saved = await saveAnnotation({ songId: parts[0], arrangementId: parts[1] || null, planId: parts[2] || null, pageIndex, strokes: notes.strokes });
          const current = await getNotes(key);
          if (current?.pending && current.updatedAt === notes.updatedAt) {
            await putNotes(key, { strokes: notes.strokes, updatedAt: saved.updatedAt, pending: false });
          }
        } catch {
          // Keep the queued strokes for the next connected visit.
        }
      }
    };
    void sync();
    window.addEventListener("online", sync);
    return () => { cancelled = true; window.removeEventListener("online", sync); };
  }, [offline]);

  useEffect(() => {
    if (!item) return;
    if (skipPageReset.current || keepPage.current) {
      skipPageReset.current = false;
      keepPage.current = false;
      return;
    }
    setPage(0);
    setPageCount(1);
    setArrangementPick(null);
    setPdfPick("");
    setAudioPick("");
    setClearArmed(false);
    scroller.current?.scrollTo({ top: 0 });
  }, [item?.id]);

  useEffect(() => {
    if (!ready || !plan || !item) return;
    const memory: StandMemory = {
      itemId: item.id,
      page,
      extra,
      capo,
      view,
      font,
      mode,
      tempo: tempos[item.id] ?? null,
      arrangementId: arrangement?.id ?? null,
    };
    writeSessionMemory(plan.id, memory);
    const handle = window.setTimeout(() => { void putPosition(plan.id, memory); }, 250);
    return () => window.clearTimeout(handle);
  }, [ready, plan, item, page, extra, capo, view, font, mode, tempos, arrangement?.id]);

  useEffect(() => {
    if (!plan) return;
    let cancelled = false;
    setOfflineReady(false);
    void getOfflineReadiness(plan).then((result) => {
      if (cancelled) return;
      setOfflineReady(result.ready);
      setCacheNote(result.ready ? "Set content saved on this device" : result.total ? `${result.available} of ${result.total} resources saved on this device.` : "Save this set on this device before performing.");
    }).catch(() => {
      if (!cancelled) setCacheNote("Offline storage is unavailable on this device.");
    });
    return () => { cancelled = true; };
  }, [plan]);

  async function downloadOfflineSet() {
    if (!plan || downloading) return;
    setDownloading(true);
    setCacheNote("Preparing offline set…");
    try {
      const result = await downloadPlanForOffline(plan, {
        onProgress: (progress) => setCacheNote(`Saving set resources: ${progress.completed} of ${progress.total}`),
      });
      setOfflineReady(result.ready);
      setCacheNote(result.ready ? "Set content saved on this device" : "The complete set could not be saved. Check your connection and try again.");
    } catch (caught) {
      setOfflineReady(false);
      setCacheNote(`Offline download failed: ${messageOf(caught)}`);
    } finally {
      setDownloading(false);
    }
  }

  useEffect(() => {
    if (!scrolling) return;
    let frame = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const node = scroller.current;
      if (!node) return;
      node.scrollTop += ((now - last) / 1000) * speed;
      last = now;
      if (node.scrollTop + node.clientHeight >= node.scrollHeight - 2) {
        setScrolling(false);
        return;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [scrolling, speed]);

  useEffect(() => {
    if (metroOn) metroRef.current?.update(tempo, beats);
  }, [metroOn, tempo, beats]);

  useEffect(() => {
    const bus = new BroadcastChannel("mblive-stand");
    channel.current = bus;
    bus.onmessage = (event: MessageEvent<FollowMessage>) => {
      const data = event.data;
      if (followRef.current !== "follow" || data.sender === sender.current || data.planId !== planId) return;
      const found = planRef.current?.items.findIndex((row) => row.id === data.itemId) ?? -1;
      if (found >= 0) {
        keepPage.current = true;
        setIndex(found);
      }
      setArrangementPick(null);
      setPdfPick("");
      setPage(data.page);
      setCapo(clamp(data.capo, 0, 11));
      setView(data.view === "pdf" ? "pdf" : "chart");
      setExtras((current) => ({ ...current, [data.itemId]: data.extra }));
      setHeard(true);
    };
    return () => bus.close();
  }, [planId]);

  useEffect(() => {
    if (follow !== "lead" || !plan || !item) return;
    const message: FollowMessage = { planId: plan.id, itemId: item.id, page, extra, capo, view, sender: sender.current };
    channel.current?.postMessage(message);
  }, [follow, plan, item, page, extra, capo, view]);

  useEffect(() => {
    if (!heard) return;
    const handle = window.setTimeout(() => setHeard(false), 1600);
    return () => window.clearTimeout(handle);
  }, [heard]);

  function chooseItem(rowIndex: number) {
    setIndex(rowIndex);
    if (window.matchMedia("(max-width: 980px)").matches) setListOpen(false);
  }

  function stepItem(direction: 1 | -1) {
    if (!plan) return;
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= plan.items.length) return;
    setIndex(nextIndex);
  }

  function go(direction: 1 | -1) {
    const node = scroller.current;
    if (!showingPdf && node) {
      const top = nextScrollTop(node.scrollTop, node.clientHeight, node.scrollHeight, direction);
      if (top !== null) {
        const smooth = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
        node.scrollTo({ top, behavior: smooth });
        return;
      }
    }
    if (showingPdf) {
      const next = nextPdfPage(page, pageCount, direction);
      if (next !== null) {
        setPage(next);
        return;
      }
    }
    if (!plan) return;
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= plan.items.length) return;
    setIndex(nextIndex);
  }

  function exit() {
    if (Capacitor.isNativePlatform()) {
      navigate("/music-stand");
      return;
    }
    if (!plan) {
      navigate("/");
      return;
    }
    navigate(practice ? `/songs/${plan.id}` : `/services/${plan.id}`);
  }

  function toggleMetro() {
    if (metroOn) {
      metroRef.current?.stop();
      setMetroOn(false);
      setBeat(0);
      return;
    }
    metroRef.current?.start(tempo, beats, (next) => { if (alive.current) setBeat(next); });
    setMetroOn(true);
  }

  function nudgeFont(delta: number) {
    fontTouched.current = true;
    setFont((current) => clamp(current + delta, 16, 48));
  }

  actions.current = { go, exit, toggleMetro, nudgeFont };
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))) return;
      if (helpRef.current && event.key === "Escape") return;
      if (event.key === "Escape" && listOpenRef.current && window.matchMedia("(max-width: 980px)").matches) {
        setListOpen(false);
        return;
      }
      if (["ArrowDown", "ArrowRight", "PageDown", " "].includes(event.key)) {
        event.preventDefault();
        actions.current.go(1);
      } else if (["ArrowUp", "ArrowLeft", "PageUp"].includes(event.key)) {
        event.preventDefault();
        actions.current.go(-1);
      } else if (event.key === "Escape") actions.current.exit();
      else if (event.key.toLowerCase() === "a") setScrolling((value) => !value);
      else if (event.key.toLowerCase() === "m") actions.current.toggleMetro();
      else if (event.key === "?") setHelp(true);
      else if (event.key === "[") actions.current.nudgeFont(-2);
      else if (event.key === "]") actions.current.nudgeFont(2);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  function onTouchStart(event: TouchEvent<HTMLDivElement>) {
    const touch = event.changedTouches[0];
    if (!touch) return;
    touchStart.current = { x: touch.clientX, y: touch.clientY };
  }

  function onTouchEnd(event: TouchEvent<HTMLDivElement>) {
    if (tool !== "pan") return;
    const start = touchStart.current;
    const touch = event.changedTouches[0];
    if (!start || !touch) return;
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    if (Math.abs(dx) < 72 || Math.abs(dx) < Math.abs(dy) * 1.15) return;
    go(dx < 0 ? 1 : -1);
  }

  const touchStart = useRef<{ x: number; y: number } | null>(null);

  if (!plan || !item) {
    return (
      <section className="stand mb-stand">
        <header className="stand-bar">
          <strong>Music stand</strong>
          <Link className="btn ghost" to={Capacitor.isNativePlatform() ? "/music-stand" : "/"}>Exit</Link>
        </header>
        <p className={error ? "notice" : "muted"} style={{ padding: 24 }} role={error ? "alert" : "status"}>{error || "Opening the stand…"}</p>
      </section>
    );
  }

  const exitHref = Capacitor.isNativePlatform() ? "/music-stand" : practice ? `/songs/${plan.id}` : `/services/${plan.id}`;
  return (
    <section className={listOpen ? "stand mb-stand" : "stand mb-stand list-closed"} style={{ ["--stand-font" as string]: `${font}px` }}>
      <p className="sr-only" aria-live="polite">{item.title}, {chart ? `shapes in ${chart.key}` : item.kind}, item {index + 1} of {plan.items.length}</p>
      <header className="stand-bar">
        <div className="cluster">
          <span className="stand-identity"><img src={logoUrl} alt="" /><span>MB Music Stand</span></span>
          <Link className="btn ghost" to={exitHref}>Exit</Link>
          <button className="btn small" type="button" aria-pressed={listOpen} onClick={() => setListOpen((value) => !value)}>{listOpen ? "Hide set" : "Setlist"}</button>
          <button className="btn small stand-controls-toggle" type="button" aria-expanded={controlsOpen} aria-controls="stand-tools" onClick={() => setControlsOpen((value) => !value)}>Controls</button>
          <div>
            <strong>{plan.title}</strong>
            <div className="faint">{practice ? "Practice" : formatDateTime(plan.startsAt)}{plan.series ? ` · ${plan.series}` : ""}</div>
          </div>
          {offline && <span className="notice">Offline copy</span>}
          <span className="stand-wake-indicator" aria-live="polite">Awake: {screenAwake.status === "on" ? "On" : screenAwake.status === "trying" ? "Starting" : screenAwake.status === "off" ? "Off" : "Unavailable"}</span>
          <button className={offlineReady ? "btn small teal" : "btn small"} type="button" onClick={() => { void downloadOfflineSet(); }} disabled={downloading || offline}>
            {downloading ? "Downloading…" : offlineReady ? "Set saved" : "Download set"}
          </button>
          {heard && <span className="badge teal">Following</span>}
        </div>
        <div id="stand-tools" className={controlsOpen ? "stand-tools controls-open" : "stand-tools"}>
          <label>Concert
            <select aria-label="Concert key" value={concert} onChange={(event) => setExtras((current) => ({ ...current, [item.id]: semitoneGap(planned, event.target.value) }))}>
              {!KEY_OPTIONS.includes(concert) && <option value={concert}>{concert}</option>}
              {KEY_OPTIONS.map((key) => <option key={key}>{key}</option>)}
            </select>
          </label>
          <label>Capo
            <input aria-label="Capo" type="number" min={0} max={11} value={capo} onChange={(event) => setCapo(clamp(Number(event.target.value) || 0, 0, 11))} style={{ width: 64 }} />
          </label>
          <select aria-label="Chord display" value={mode} onChange={(event) => setMode(event.target.value as ChartMode)}>
            {CHART_MODES.map((row) => <option key={row.id} value={row.id}>{row.label}</option>)}
          </select>
          <button className="btn small" type="button" onClick={() => nudgeFont(-2)}>A−</button>
          <button className="btn small" type="button" onClick={() => nudgeFont(2)}>A+</button>
          {pdf && <button className={showingPdf ? "btn small primary" : "btn small"} type="button" onClick={() => setView(showingPdf ? "chart" : "pdf")}>{showingPdf ? "Lyrics" : "PDF"}</button>}
          <select aria-label="Tab sync" value={follow} onChange={(event) => setFollow(event.target.value as "off" | "lead" | "follow")}>
            <option value="off">This tab</option>
            <option value="lead">Lead other tabs</option>
            <option value="follow">Follow a tab</option>
          </select>
          <button className="btn small ghost" type="button" onClick={() => setHelp(true)}>Keys</button>
          <label className="check-line" title="Prevents automatic screen sleep during this performance view. Uses more battery.">
            <input type="checkbox" checked={screenAwake.enabled} onChange={(event) => screenAwake.setEnabled(event.target.checked)} />
            Keep screen awake: {screenAwake.status === "on" ? "On" : screenAwake.status === "off" ? "Off" : screenAwake.status === "trying" ? "Starting" : "Unavailable"}
          </label>
        </div>
      </header>
      <div className="stand-stage">
        {listOpen && <button className="set-backdrop" type="button" aria-label="Close setlist" onClick={() => setListOpen(false)} />}
        <nav className="stand-list" id="stand-setlist" aria-label="Setlist">
          {plan.items.map((row, rowIndex) => (
            <button className={rowIndex === index ? "active" : ""} type="button" key={row.id} aria-current={rowIndex === index ? "true" : undefined} onClick={() => chooseItem(rowIndex)}>
              <strong>{row.title}</strong>
              <small>{practice ? row.arrangementName ?? KINDS[row.kind] : `${formatTime(row.startsAt)} · ${row.keyName ?? KINDS[row.kind]}`}</small>
            </button>
          ))}
          {plan.notes && <p className="stand-notes">{plan.notes}</p>}
          {plan.totalSeconds > 0 && <p className="faint">{durationLabel(plan.totalSeconds)} in the set</p>}
        </nav>
        <div className="stand-read">
          <div className="set-jump">
            <button className="btn small" type="button" onClick={() => stepItem(-1)} disabled={index === 0}>Previous</button>
            <button className="set-jump-title" type="button" aria-expanded={listOpen} aria-controls="stand-setlist" onClick={() => setListOpen(true)}>
              <span>{index + 1} / {plan.items.length}</span>
              <strong>{item.title}</strong>
            </button>
            <button className="btn small" type="button" onClick={() => stepItem(1)} disabled={index >= plan.items.length - 1}>Next</button>
          </div>
          <div className="ink-tools">
            <button className="btn small pager-wide" type="button" onClick={() => go(-1)}>Previous</button>
            <button className="btn small pager-wide" type="button" onClick={() => go(1)}>Next</button>
            <span className="muted">{item.bpm ? `${item.bpm} bpm` : "No song tempo"} · {meter}{chart ? ` · shapes in ${chart.key}` : ""}{capo > 0 ? ` · capo ${capo}` : ""}</span>
            {sameSong && library && library.arrangements.length > 1 && (
              <select aria-label="Arrangement" value={arrangement?.id ?? ""} onChange={(event) => setArrangementPick(event.target.value)}>
                {library.arrangements.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
              </select>
            )}
            {canMark && (
              <>
                {(["pan", "pen", "highlight"] as const).map((next) => (
                  <button key={next} className={tool === next ? "btn small primary" : "btn small"} type="button" aria-pressed={tool === next} onClick={() => setTool(next)}>{next === "pan" ? "Move" : next === "pen" ? "Pen" : "Highlight"}</button>
                ))}
                {palette.map((swatch) => (
                  <button key={swatch} className="swatch" type="button" aria-label={`Ink ${swatch}`} aria-pressed={ink === swatch} style={{ background: swatch }} onClick={() => setColor(swatch)} />
                ))}
                <label className="faint">Weight
                  <input aria-label="Stroke weight" type="range" min={tool === "highlight" ? 8 : 1} max={tool === "highlight" ? 28 : 8} step={0.5} value={width} onChange={(event) => {
                    const next = Number(event.target.value);
                    if (tool === "highlight") setMarkWidth(next);
                    else setPenWidth(next);
                  }} />
                </label>
                <button className="btn small" type="button" onClick={marks.undo} disabled={marks.strokes.length === 0}>Undo</button>
                <button className="btn small" type="button" onClick={() => { if (!clearArmed) { setClearArmed(true); return; } marks.clear(); setClearArmed(false); }}>{clearArmed ? "Confirm clear" : "Clear"}</button>
              </>
            )}
            {showingPdf && (
              <>
                <span>Page {Math.min(page + 1, pageCount)} / {pageCount}</span>
                <button className="btn small" type="button" onClick={() => setZoom((value) => clamp(Number((value + 0.2).toFixed(2)), 0.6, 2.6))}>Zoom in</button>
                <button className="btn small" type="button" onClick={() => setZoom((value) => clamp(Number((value - 0.2).toFixed(2)), 0.6, 2.6))}>Zoom out</button>
                <button className={night ? "btn small teal" : "btn small"} type="button" onClick={() => setNight((value) => !value)}>{night ? "Night PDF" : "Paper PDF"}</button>
                {pdfs.length > 1 && (
                  <select aria-label="Chart file" value={pdf?.id ?? ""} onChange={(event) => { setPdfPick(event.target.value); setPage(0); }}>
                    {pdfs.map((file) => <option key={file.id} value={file.id}>{file.label}</option>)}
                  </select>
                )}
              </>
            )}
          </div>
          <div
            className="stand-scroll"
            ref={scroller}
            onWheel={() => { if (scrolling) setScrolling(false); }}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <div className="sheet-frame">
              {showingPdf && pdf && <PdfChart url={pdf.url} fileId={pdf.id} page={page + 1} zoom={zoom} night={night} onPages={rememberPages} />}
              {!showingPdf && chart && <ChartView blocks={parseChart(chart.chart)} mode={mode} numberKey={chart.key} />}
              {!showingPdf && !chart && (
                <div className="stage-card">
                  <p className="kicker">{KINDS[item.kind] ?? item.kind}</p>
                  <h2>{item.title}</h2>
                  {item.description && <p className="lede">{item.description}</p>}
                  {!item.songId && <p className="faint">This item has no chart. Next moves on through the set.</p>}
                  {item.songId && !chordpro.trim() && <p className="faint">This song has no ChordPro chart yet.</p>}
                </div>
              )}
              {canMark && <AnnotationLayer strokes={marks.strokes} tool={tool} color={ink} width={width} onCommit={marks.commit} />}
            </div>
            {item.description && chart && !showingPdf && <p className="muted" style={{ marginTop: 16 }}>{item.description}</p>}
          </div>
          <footer className="transport">
            <button className={scrolling ? "btn teal small" : "btn small"} type="button" onClick={() => setScrolling((value) => !value)}>{scrolling ? "Stop scroll" : "Autoscroll"}</button>
            <label className="faint">Pace
              <input aria-label="Autoscroll speed" type="range" min={12} max={140} value={speed} onChange={(event) => setSpeed(Number(event.target.value))} />
            </label>
            <button className={metroOn ? "btn teal small" : "btn small"} type="button" onClick={toggleMetro}>{metroOn ? "Stop click" : "Metronome"}</button>
            <label className="faint">Tempo
              <input aria-label="Metronome tempo" type="number" min={20} max={300} value={tempo} onChange={(event) => setTempos((current) => ({ ...current, [item.id]: clamp(Number(event.target.value) || 96, 20, 300) }))} style={{ width: 76 }} />
            </label>
            <span className="beat-row" aria-hidden="true">{Array.from({ length: beats }, (_, place) => <i key={place} className={metroOn && beat === place ? "beat on" : "beat"} />)}</span>
            {audio && <GuideAudio url={audio.url} fileId={audio.id} mime={audio.mime} label={audio.label} />}
            {audios.length > 1 && (
              <select aria-label="Audio track" value={audio?.id ?? ""} onChange={(event) => setAudioPick(event.target.value)}>
                {audios.map((file) => <option key={file.id} value={file.id}>{file.label}</option>)}
              </select>
            )}
            <span className="faint">{marks.status || cacheNote || wake}</span>
          </footer>
        </div>
      </div>
      {help && (
        <Dialog title="Stand controls" onClose={() => setHelp(false)}>
          <div className="help-list">
            <p><kbd>Space</kbd> <kbd>Page Down</kbd> <kbd>→</kbd> scroll, then the next page or song.</p>
            <p><kbd>Page Up</kbd> <kbd>←</kbd> move backward. Pedals that send these keys work the same way.</p>
            <p><kbd>A</kbd> autoscroll. <kbd>M</kbd> metronome. <kbd>[</kbd> <kbd>]</kbd> text size. <kbd>Esc</kbd> exits.</p>
            <p>Swipe sideways to change songs. Draw only after you choose Pen or Highlight, then the marks save for this song, arrangement, and page.</p>
            <p>Lead and Follow talk between MB Live tabs on this computer. They are not a network session.</p>
            <p>{wake} Keeping the screen on uses more battery. The device can still lock manually or during system restrictions.</p>
            <p>{offline ? "You are reading a saved copy on this device." : cacheNote || "Use Download set to save its content on this device. Reopen the app without a connection before relying on it on stage."}</p>
          </div>
        </Dialog>
      )}
    </section>
  );
}
